import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHelmet from '@/components/feature/SEOHelmet';
import { categories, categoryStats } from '@/mocks/categories';
import { resources } from '@/mocks/resources';

export default function CategoryPage() {
  const { slug = 'anime' } = useParams();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [filteredResources, setFilteredResources] = useState(resources.filter(r => r.category === slug));

  useEffect(() => {
    setActiveCategory(slug);
    setFilteredResources(resources.filter(r => r.category === slug));
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];
  const stats = categoryStats[slug as keyof typeof categoryStats] || { count: 0, today: 0 };

  return (
    <>
      <SEOHelmet
        title={`${currentCategory.name} - 宅宅资源库 | 二次元${currentCategory.name}资源下载`}
        description={`宅宅资源库${currentCategory.name}专区，收录${stats.count}+${currentCategory.name}资源，每日更新。${currentCategory.description}`}
        keywords={`${currentCategory.name},二次元资源,宅宅资源库,${currentCategory.name}下载,动漫资源聚合`}
        canonicalUrl={`https://doujincenter.github.io/category/${slug}`}
      />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: currentCategory.slug },
        ]} />
      </div>

      <main>
        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${currentCategory.icon} text-primary-600 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">{currentCategory.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              </div>
              <div className="ml-auto flex items-center gap-4 text-sm">
                <span className="text-foreground-500">共 <strong className="text-primary-600">{stats.count}</strong> 个资源</span>
                <span className="text-accent-600">今日更新 <strong>{stats.today}</strong></span>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200/70'
                  }`}
                  onClick={() => setActiveCategory(cat.slug)}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Resource Grid */}
        <section className="w-full px-4 md:px-6 py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredResources.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredResources.map(resource => (
                  <Link
                    key={resource.id}
                    to={`/article/${resource.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative w-full aspect-[4/3] overflow-hidden">
                      <img
                        src={resource.image}
                        alt={resource.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        title={`${resource.title} - 宅宅资源库`}
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                        {resource.rating}
                      </div>
                      {resource.featured && (
                        <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                          热门
                        </div>
                      )}
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 mb-2 leading-snug group-hover:text-primary-600 transition-colors duration-150">
                        {resource.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1 mb-3">{resource.desc}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{resource.size}</span>
                        <span>{resource.downloads} 下载</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-foreground-400 text-3xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无资源，敬请期待！</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}